import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useCookies } from 'react-cookie'
import App from './App'
import Login from './pages/Login'
import AuthCallback from './pages/AuthCallback'
import { useHistory, useLocation } from 'react-router-dom'
import Api from './api'
import { configureApi, setLogoutCallback } from './api/config'
import { loggedUser, logout } from './redux/actions/user'
import { userPermissions } from './redux/actions/permissions'
import { userSetups } from './redux/actions/userSetups'
import CenteredLoading from './components/CenteredLoading'
import SSOHandler from './components/authentication/SSOHandler'
import { msalInstance, loginRequest } from './authConfig'
import { getEnvFlag } from './env'
import {
  loadStateFromLocalStorage,
  clearStateFromLocalStorage,
  saveStateToLocalStorage,
} from './redux/utilsLocalStorage'
import { jwtDecode } from 'jwt-decode'

// Insert 's' at position 30
function encodeToken(token) {
  return token.slice(0, 30) + 's' + token.slice(30)
}
// Remove 's' at position 30
function decodeToken(token) {
  return token.slice(0, 30) + token.slice(31)
}

const isSsoEnabled = getEnvFlag('REACT_APP_ENABLE_AAD_LOGIN')

const IndexApp = props => {
  const dispatch = useDispatch()
  const [cookies, setCookie, removeCookie] = useCookies(['authToken'])
  const [errorMessage, setErrorMessage] = useState('')
  const [authenticated, setAuthenticated] = useState(null)
  const [initializing, setInitializing] = useState(true)
  const [loginLoading, setLoginLoading] = useState(false)
  const [ssoError, setSsoError] = useState('')
  const [ssoLoading, setSsoLoading] = useState(false)
  const [failedLoginCounter, setFailedLoginCounter] = useState(0)
  const history = useHistory()
  const location = useLocation()
  const logoutTimerRef = useRef(null)
  const isSsoSessionRef = useRef(false)
  const handleSsoLoginRef = useRef(null)

  const logoutUser = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }

    clearStateFromLocalStorage()
    setAuthenticated(false)
    dispatch(logout())

    if (isSsoSessionRef.current && msalInstance) {
      isSsoSessionRef.current = false
      // App-only logout: the Equinor tenant is federated to Okta, so a full
      // logoutRedirect triggers a single sign-out cleanup chain (Azure → Okta →
      // Azure) that never returns to the app. Returning false from
      // onRedirectNavigate makes MSAL clear its local session WITHOUT
      // redirecting to the IdP. The Okta/Microsoft session stays alive, so we
      // flag SSOHandler not to silently sign the user back in on the next load.
      sessionStorage.setItem('sso_skip_silent', '1')
      msalInstance
        .logoutRedirect({
          account:
            msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0],
          onRedirectNavigate: () => false,
        })
        .finally(() => {
          removeCookie('authToken', { path: '/' })
          history.push('/')
        })
    } else {
      removeCookie('authToken', { path: '/' })
      history.push('/')
    }
  }, [removeCookie, dispatch, history])

  // Register the logout callback for API interceptor (handles 401 Token expired)
  useEffect(() => {
    setLogoutCallback(logoutUser)
    return () => setLogoutCallback(null)
  }, [logoutUser])

  const scheduleAutoLogout = useCallback(
    (token, isEncodedCookie = false) => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
      }

      try {
        const rawToken = isEncodedCookie ? decodeToken(token) : token
        const decoded = jwtDecode(rawToken)
        const expMs = decoded.exp * 1000
        const nowMs = Date.now()
        const timeoutMs = expMs - nowMs

        if (timeoutMs > 0) {
          logoutTimerRef.current = setTimeout(async () => {
            if (isSsoSessionRef.current && msalInstance) {
              try {
                const response = await msalInstance.ssoSilent(loginRequest)
                await handleSsoLoginRef.current?.(response.idToken)
              } catch {
                // Entra session also expired — full logout
                logoutUser()
              }
            } else {
              logoutUser()
            }
          }, timeoutMs)
        } else {
          logoutUser()
        }
      } catch (e) {
        // If decoding fails, log out for safety
        logoutUser()
      }
    },
    [logoutUser],
  )

  /** This is the way to split user in separated API call */
  useEffect(() => {
    const initializeUser = async () => {
      if (cookies.authToken) {
        try {
          configureApi(decodeToken(cookies.authToken))
          const storedPermissions = loadStateFromLocalStorage('permissions')

          // If we have permissions, proceed with initialization
          if (storedPermissions) {
            dispatch(userPermissions(storedPermissions))

            const setupsResponse = await Api.Setups.getUserSetups()
            const setups = setupsResponse?.data?.setups

            if (Array.isArray(setups)) {
              /** Filter setups that belong to the user based on their enabled status */
              const filterEnabled = setups.filter(setup => setup.enabled)
              const filterPower = setups.filter(
                setup => setup.enabled && setup.is_power === true,
              )
              dispatch(
                userSetups(filterEnabled, filterPower, storedPermissions),
              )
              setAuthenticated(true)
              scheduleAutoLogout(cookies.authToken, true)
            } else {
              throw new Error('Invalid or missing user setups')
            }
          } else {
            logoutUser()
          }
        } catch (error) {
          console.error('Error during initialization:', error?.message || error)
          setAuthenticated(false)
        }
        setInitializing(false)
      } else {
        if (!isSsoEnabled) {
          // No SSO: immediately show login form
          setAuthenticated(false)
          setInitializing(false)
        }
        // SSO enabled: keep initializing=true — SSOHandler drives the rest
      }
    }

    initializeUser()
  }, [cookies.authToken])

  const authenticate = async (formData, rememberLogin) => {
    try {
      setLoginLoading(true)
      const responseAuth = await Api.authenticateUser(formData)

      const { access_token, permissions, user_id, timezone, unit } =
        responseAuth.data

      if (rememberLogin) {
        saveToken(access_token)
      }
      configureApi(access_token)

      // Save permissions to localStorage before dispatching
      saveStateToLocalStorage(permissions, 'permissions')

      // setups that belongs to the user
      const userSetupsResponse = await Api.Setups.getUserSetups()

      const setupsFromUser = Array.isArray(userSetupsResponse?.data?.setups)
        ? userSetupsResponse.data.setups.filter(setup => setup.enabled)
        : []

      const powerSetupsFromUser = Array.isArray(
        userSetupsResponse?.data?.setups,
      )
        ? userSetupsResponse.data.setups.filter(
            setup => setup.enabled && setup.is_power === true,
          )
        : []

      if (!setupsFromUser.length) {
        console.error(
          `No user setups returned from API. Response: ${JSON.stringify(
            userSetupsResponse?.data?.setups,
          )}`,
        )
        throw new Error('No user setups returned from API.')
      }

      const responseUser = {
        username: formData.name,
        password: formData.password,
        permissions: permissions,
        setups: setupsFromUser,
        userId: user_id,
        timezone: timezone || 'Etc/UTC',
        unit: unit,
      }

      dispatch(loggedUser(responseUser))
      dispatch(userPermissions(permissions))
      dispatch(userSetups(setupsFromUser, powerSetupsFromUser, permissions))

      setAuthenticated(true)
      setLoginLoading(false)
      scheduleAutoLogout(access_token)
    } catch (error) {
      setLoginLoading(false)
      if (failedLoginCounter < 2) {
        setErrorMessage('Incorrect username and/or password. Please try again.')
        setFailedLoginCounter(failedLoginCounter + 1)
      } else {
        setErrorMessage(
          'Incorrect username and/or password. Please try again or contact your administrator to reset your password.',
        )
      }
      setAuthenticated(false)
      logoutUser()
    }
  }

  const handleSsoLogin = useCallback(
    async idToken => {
      try {
        setInitializing(true)

        const response = await Api.authenticateOidc(idToken)
        const { access_token, permissions, user_id, timezone, unit } =
          response.data

        configureApi(access_token)
        isSsoSessionRef.current = true

        saveStateToLocalStorage(permissions, 'permissions')

        // Username isn't in the /auth/oidc response — fetch it from the
        // authenticated /preferences endpoint (the same source the profile
        // menu uses), so SSO and regular logins show the same canonical name.
        const prefsResponse = await Api.Users.getUserPreferences()
        const username = prefsResponse?.data?.username || ''

        const userSetupsResponse = await Api.Setups.getUserSetups()
        const setups = userSetupsResponse?.data?.setups || []
        const filterEnabled = setups.filter(s => s.enabled)
        const filterPower = setups.filter(s => s.enabled && s.is_power === true)

        dispatch(
          loggedUser({
            username,
            permissions,
            setups: filterEnabled,
            userId: user_id,
            timezone: timezone || 'Etc/UTC',
            unit,
          }),
        )
        dispatch(userPermissions(permissions))
        dispatch(userSetups(filterEnabled, filterPower, permissions))

        setAuthenticated(true)
        scheduleAutoLogout(access_token)
        return true
      } catch (error) {
        console.error('SSO login failed:', error?.message || error)
        setAuthenticated(false)
        return false
      } finally {
        setInitializing(false)
      }
    },
    [dispatch, scheduleAutoLogout],
  )

  // Keep the ref pointing at the latest handleSsoLogin (avoids circular dep in scheduleAutoLogout)
  useEffect(() => {
    handleSsoLoginRef.current = handleSsoLogin
  }, [handleSsoLogin])

  const handleSsoFail = useCallback(() => {
    // Only unblock initializing if there is no cookie session already handling it
    if (!cookies.authToken) {
      setAuthenticated(false)
      setInitializing(false)
    }
  }, [cookies.authToken])

  // Interactive Equinor sign-in via popup: keeps the app loaded so a failed
  // sign-in (e.g. an unauthorized account) returns here with a message instead
  // of stranding the user on Microsoft's error page.
  const loginWithEquinor = useCallback(async () => {
    if (!msalInstance) return
    setSsoError('')
    setSsoLoading(true)
    try {
      const result = await msalInstance.loginPopup({
        ...loginRequest,
        prompt: 'select_account',
        popupWindowAttributes: {
          popupSize: { width: 600, height: 700 },
        },
      })
      const ok = await handleSsoLogin(result.idToken)
      if (!ok) {
        setSsoError(
          'This account is not authorized for this application. Try a different account.',
        )
      }
    } catch (e) {
      // Closing/cancelling the popup is not an error — stay silent. Only show a
      // message for real failures (e.g. the popup being blocked by the browser).
      if (e?.errorCode !== 'user_cancelled') {
        setSsoError('Sign-in failed. Please try again.')
      }
    } finally {
      setSsoLoading(false)
    }
  }, [handleSsoLogin])

  const saveToken = useCallback(
    token => {
      const expirationDate = new Date()
      const oneDayMs = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
      const safetyMarginMs = 5 * 60 * 1000 // 4 minutes in milliseconds
      /**
       * Set the expiration date to 5 minutes less than 24 hours from now.
       * This ensures the frontend never keeps an expired token.
       */
      expirationDate.setTime(
        expirationDate.getTime() + oneDayMs - safetyMarginMs,
      )
      setCookie('authToken', encodeToken(token), {
        path: '/',
        expires: expirationDate,
        secure: true, // Only send over HTTPS
        sameSite: 'strict', // CSRF protection
      })
    },
    [setCookie],
  )

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
        logoutTimerRef.current = null
      }
    }
  }, [])

  if (isSsoEnabled && location.pathname === '/auth/callback') {
    return <AuthCallback />
  }

  return (
    <>
      {isSsoEnabled && !authenticated && (
        <SSOHandler onSuccess={handleSsoLogin} onFail={handleSsoFail} />
      )}
      {initializing ? (
        <CenteredLoading />
      ) : authenticated ? (
        <App logout={logoutUser} />
      ) : (
        <Login
          loading={loginLoading}
          onSubmitUser={authenticate}
          onSsoLogin={loginWithEquinor}
          ssoLoading={ssoLoading}
          ssoError={ssoError}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      )}
    </>
  )
}

export default IndexApp
